<template>
  <div 
    v-if="visible" 
    class="annotation-popup"
    :style="popupStyle"
    @click.stop
  >
    <div class="popup-header">
      <span class="popup-title">{{ $t('annotate.change_label') }}</span>
      <v-btn 
        icon 
        small 
        @click="close"
        class="close-btn"
      >
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>
    <div class="popup-content">
      <annotate-labels 
        :type="annotationType" 
        @add_annotation="handleLabelChange"
        :placeholder="$t('annotate.select_new_label')"
      ></annotate-labels>
    </div>
  </div>
</template>

<script>
import AnnotateLabels from "@/components/AnnotateLabels";

export default {
  name: "AnnotationRightClickPopup",
  components: {
    AnnotateLabels
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    position: {
      type: Object,
      default: () => ({ x: 0, y: 0 })
    },
    annotation: {
      type: Object,
      default: null
    }
  },
  computed: {
    popupStyle() {
      // Calculate position to avoid going off-screen
      let x = this.position.x;
      let y = this.position.y;
      
      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Estimate popup dimensions (adjust as needed)
      const popupWidth = 250;
      const popupHeight = 120;
      
      // Adjust X position if popup would go off the right edge
      if (x + popupWidth > viewportWidth) {
        x = viewportWidth - popupWidth - 10;
      }
      
      // Adjust Y position if popup would go off the bottom edge
      if (y + popupHeight > viewportHeight) {
        y = viewportHeight - popupHeight - 10;
      }
      
      // Ensure popup doesn't go off the left or top edges
      x = Math.max(10, x);
      y = Math.max(10, y);
      
      return {
        position: 'absolute',
        left: x + 'px',
        top: y + 'px',
        zIndex: 1000
      };
    },
    annotationType() {
      return this.annotation ? this.annotation.type : 'box';
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    handleLabelChange(newAnnotation) {
      if (this.annotation && newAnnotation) {
        // Update the existing annotation with the new label
        const updatedAnnotation = {
          ...this.annotation,
          label: newAnnotation.label,
          color_values: newAnnotation.color_values
        };
        this.$emit('update_annotation', updatedAnnotation);
        this.close();
      }
    },
    handleOutsideClick(event) {
      if (this.visible && !this.$el.contains(event.target)) {
        this.close();
      }
    },
    handleKeyDown(event) {
      if (this.visible && event.key === 'Escape') {
        this.close();
      }
    }
  },
  mounted() {
    // Close popup when clicking outside
    document.addEventListener('click', this.handleOutsideClick);
    // Close popup when pressing ESC
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeyDown);
  }
};
</script>

<style scoped>
.annotation-popup {
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  max-width: 300px;
  user-select: none;
  pointer-events: auto;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  background: #f5f5f5;
}

.popup-title {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.close-btn {
  margin: 0;
  padding: 0;
}

.popup-content {
  padding: 12px;
}
</style> 